type Callback<T, V = string> = (r: T) => (V | undefined | null);
type Sorting<T> = [(t: T) => any, boolean]

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function compare<T>(a: T, b: T, val: (t: T) => any) {
  var aValue = val(a);
  var bValue = val(b);
  return aValue > bValue ? 1 : (aValue < bValue ? -1 : 0);
}

abstract class ListCacheBase<T> {
  cache: Record<string, T | null>

  constructor(cache: Record<string, T | null>) {
    this.cache = cache;
  }

  abstract getItems() : T[]

  charSeparatedListContains(list: string | undefined | null, val: Callback<T>, char: string = ',') {
    if (!list) {
      return new FilteredListCache(this.getItems(), this.cache);
    }

    var parts = list.split(char);
    return this.listContains(parts, val);
  }

  listContains<V>(list: V[] | undefined | null, val: Callback<T, V>) {
    if (!list || list.length == 0) {
      return new FilteredListCache(this.getItems(), this.cache);
    }

    var items = this.getItems().filter(r => {
      var v = val(r);
      if (!v) {
        return false;
      }
      return list.indexOf(v) >= 0;
    })

    return new FilteredListCache(items, this.cache);
  }


  containsText(text: string | undefined | null, ...vals: Callback<T>[]) {
    if (!text) {
      return new FilteredListCache(this.getItems(), this.cache);
    }

    var items = this.getItems().filter(r => {
      var match = false;

      for (var callback of vals) {
        var v = callback(r);
        if (!v) {
          continue;
        }
        if (v.indexOf(text) >= 0) {
          match = true;
          break;
        }
      }
      return match;
    });

    return new FilteredListCache(items, this.cache);
  }

  mergeServerData(data: T[], key: Callback<T>, pageSize: number, sorting: Sorting<T>) {
    const items = this.getItems().slice();

    // Clean up cache of filter out cashed items
    data = data.filter(r => {
      var id = key(r);
      if (id && this.cache[id]) {
        if (JSON.stringify(r) == JSON.stringify(this.cache[id])) {
          var index = items.indexOf(this.cache[id]!);
          if (index >= 0) {
            items.splice(index, 1);
          }
          delete this.cache[id];
        }
        else {
          return false;
        }
      }
      return true;
    });

    // Remove deleted items
    for (var id of Object.keys(this.cache).filter(r => this.cache[r] == null)) {
      var index = data.findIndex(r => key(r) == id);
      if (index >= 0) {
        data.splice(index, 1);
      }
    }

    // Order filtered items
    items.sort((a, b) => {
      return compare(a, b, sorting[0])
    });

    // Combine items in compliance with the order
    for (var i = 0; i < data.length; i++) {
      while (items.length > 0 && compare(items[0], data[i], sorting[0]) * (sorting[1] ? -1 : 1) < 0) {
        data.splice(i, 0, items[0]);
        items.splice(0, 1);
        ++i;
      }
    }

    // Append remaining items for the last page
    if (data.length < pageSize) {
      data = data.concat(items);
    }

    return data;
  }
}


export class ListCache<T> extends ListCacheBase<T>{
  constructor() {
    super({});
  }

  getItems() {
    return Object.values(this.cache).filter(notEmpty);
  }

  set(id: string, item: T) {
    this.cache[id] = item;
  }

  del(id: string) {
    this.cache[id] = null;
  }
}

class FilteredListCache<T> extends ListCacheBase<T> {
  items: T[]

  constructor(items: T[], cache: Record<string, T | null>) {
    super(cache);
    this.items = items;
  }

  getItems() {
    return this.items;
  }
}